import React, { useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type {
	firstGroupIssues_issueTableGrouping_childIssuesQuery,
	firstGroupIssues_issueTableGrouping_childIssuesQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/firstGroupIssues_issueTableGrouping_childIssuesQuery.graphql';
import { ChildRowsPagination } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows-pagination/index.tsx';
import type { FormPropsType } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import type {
	RenderRows,
	RenderCreateIssueController,
	RenderDraggableWrapper,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export type Props = {
	variables: firstGroupIssues_issueTableGrouping_childIssuesQuery$variables;
	groupId: string;
	groupConnectionId: string;
	groupFieldValue: string | null;
	isFormVisible?: boolean;
	formProps?: FormPropsType;
	childItemsExperience: string;
	childItemsLoadMoreExperience: string;
	analyticsSource: string;
	pageSize: number;
	width: number;
	isIssueRankDescending: boolean;
	renderRows: RenderRows;
	renderCreateIssueController: RenderCreateIssueController;
	renderDraggableWrapper?: RenderDraggableWrapper;
};

export const FirstGroupIssues = ({
	variables,
	groupId,
	groupConnectionId,
	groupFieldValue,
	isFormVisible,
	formProps,
	childItemsExperience,
	childItemsLoadMoreExperience,
	analyticsSource,
	pageSize,
	width,
	isIssueRankDescending,
	renderRows,
	renderCreateIssueController,
	renderDraggableWrapper,
}: Props) => {
	const data = useLazyLoadQuery<firstGroupIssues_issueTableGrouping_childIssuesQuery>(
		graphql`
			query firstGroupIssues_issueTableGrouping_childIssuesQuery(
				$cloudId: ID!
				$issueSearchInput: JiraIssueSearchInput!
				$first: Int
				$last: Int
				$before: String
				$after: String
				$namespace: String
				$viewId: String
				$options: JiraIssueSearchOptions
				$filterId: String
				$amountOfColumns: Int!
				$fieldSetIds: [String!]!
				$fieldSetsInput: JiraIssueSearchFieldSetsInput!
				$shouldQueryFieldSetsById: Boolean!
				$fieldSetsContext: JiraIssueSearchViewFieldSetsContext
				$viewConfigInput: JiraIssueSearchViewConfigInput
				$shouldQueryHasChildren: Boolean!
				$isTimelineEnabled: Boolean!
			) {
				...childRowsPagination_issueTableHierarchy_query
					@arguments(
						isTimelineEnabled: $isTimelineEnabled
						shouldQueryHasChildren: $shouldQueryHasChildren
					)
				jira {
					issueSearch(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						first: $first
						last: $last
						before: $before
						after: $after
						options: $options
						fieldSetsInput: $fieldSetsInput
						viewConfigInput: $viewConfigInput
					)
						@connection(key: "hierarchy_nativeIssueTable__issueSearch")
						@optIn(to: "JiraSpreadsheetComponent-M1") {
						edges {
							__typename
						}
					}
				}
			}
		`,
		variables,
		{ fetchPolicy: 'store-only' },
	);

	const connectionMetadata = useMemo(
		() =>
			variables.issueSearchInput.jql
				? {
						type: 'GROUP_CHILDREN' as const,
						fieldValue: groupFieldValue,
						groupId,
						jql: variables.issueSearchInput.jql,
					}
				: undefined,
		[groupFieldValue, groupId, variables.issueSearchInput.jql],
	);

	// Ensure relay cache is in correct state
	if (!data?.jira?.issueSearch?.edges) {
		return null;
	}

	return (
		<ChildRowsPagination
			query={data}
			depth={0}
			parentItemId={groupId}
			groupId={groupId}
			itemsConnectionId={groupConnectionId}
			connectionMetadata={connectionMetadata}
			width={width}
			isIssueRankDescending={isIssueRankDescending}
			pageSize={pageSize}
			childItemsExperience={childItemsExperience}
			childItemsLoadMoreExperience={childItemsLoadMoreExperience}
			analyticsSource={analyticsSource}
			renderRows={renderRows}
			renderCreateIssueController={renderCreateIssueController}
			{...(fg('jsc_list_reranking_gate_technical') && {
				renderDraggableWrapper,
			})}
			isFormVisible={isFormVisible}
			formProps={formProps}
		/>
	);
};
